/**
 * Notification Route Resolution Configuration
 * 
 * Category-Based Route Resolution (Option 3)
 * - Defines default routes for each of the 8 notification categories
 * - Adds type-specific overrides only where special handling is needed
 * - Handles modal navigation, tab selection, and fallback routes
 * 
 * This approach balances maintainability with flexibility:
 * - New notification types automatically get sensible defaults
 * - Only exceptions need explicit configuration
 */

import { NOTIFICATION_TYPES, NOTIFICATION_CATEGORIES } from '../api/notifications';

// ==================== HELPER FUNCTIONS ====================

/**
 * Extract MongoDB ObjectId from a URL path
 * @param {string} url - The URL to parse
 * @param {string} prefix - The path prefix (e.g., '/clubs', '/programs')
 * @returns {string|null} - The extracted ID or null
 */
const extractId = (url, prefix) => {
  if (!url) return null;
  const regex = new RegExp(`${prefix}[/]?([a-f0-9]{24})`, 'i');
  const match = url.match(regex);
  return match?.[1] || null;
};

/**
 * Extract multiple IDs from notification
 * @param {Object} notification - The notification object
 * @param {string} actionUrl - The action URL
 * @returns {Object} - Object containing all extracted IDs
 */
const extractIds = (notification, actionUrl) => {
  return {
    clubId: extractId(actionUrl, '/clubs?') || notification?.data?.clubId,
    programId: extractId(actionUrl, '/programs?') || notification?.data?.programId,
    teamId: extractId(actionUrl, '/teams?') || notification?.data?.teamId,
    eventId: extractId(actionUrl, '/events?') || notification?.data?.eventId,
    equipmentId: extractId(actionUrl, '/equipment') || notification?.data?.equipmentId,
    assignmentId: extractId(actionUrl, '/assignments?') || notification?.data?.assignmentId,
    userId: notification?.data?.userId || notification?.triggeredBy?._id,
  };
};

// ==================== CATEGORY DEFAULT ROUTES ====================

/**
 * Default route generators for each notification category
 * These provide sensible defaults when no type-specific override exists
 */
const CATEGORY_DEFAULTS = {
  [NOTIFICATION_CATEGORIES.CLUB]: (ids) => {
    if (ids.clubId) {
      return { url: `/club/${ids.clubId}` };
    }
    return { url: '/dashboard' };
  },

  [NOTIFICATION_CATEGORIES.PROGRAM]: (ids) => {
    if (ids.programId && ids.clubId) {
      return { url: `/clubs/${ids.clubId}/programs/${ids.programId}` };
    }
    if (ids.clubId) {
      return { url: `/club/${ids.clubId}` };
    }
    return { url: '/dashboard' };
  },

  [NOTIFICATION_CATEGORIES.EVENT]: (ids) => {
    if (ids.eventId && ids.clubId) {
      return { url: `/clubs/${ids.clubId}/events/${ids.eventId}` };
    }
    if (ids.clubId) {
      return { url: `/club/${ids.clubId}` };
    }
    return { url: '/dashboard' };
  },

  [NOTIFICATION_CATEGORIES.RSVP]: (ids) => {
    if (ids.eventId && ids.clubId) {
      return { url: `/clubs/${ids.clubId}/events/${ids.eventId}` };
    }
    if (ids.clubId) {
      return { url: `/club/${ids.clubId}` };
    }
    return { url: '/dashboard' };
  },

  [NOTIFICATION_CATEGORIES.EQUIPMENT]: (ids) => {
    if (ids.clubId) {
      return { url: `/club/${ids.clubId}`, state: { activeTab: 'equipment' } };
    }
    return { url: '/dashboard' };
  },

  [NOTIFICATION_CATEGORIES.TEAM]: (ids) => {
    if (ids.teamId) {
      return { url: `/teams/${ids.teamId}` };
    }
    if (ids.clubId) {
      return { url: `/club/${ids.clubId}` };
    }
    return { url: '/teams' };
  },

  [NOTIFICATION_CATEGORIES.ASSIGNMENT]: (ids) => {
    if (ids.assignmentId && ids.clubId) {
      return { url: `/clubs/${ids.clubId}/assignments/${ids.assignmentId}` };
    }
    if (ids.clubId) {
      return { url: `/club/${ids.clubId}` };
    }
    return { url: '/dashboard' };
  },

  [NOTIFICATION_CATEGORIES.SYSTEM]: () => {
    return { url: '/profile/settings' };
  },
};

// ==================== TYPE-SPECIFIC OVERRIDES ====================

/**
 * Type-specific route overrides
 * Only define overrides for notifications that need special handling:
 * - Opening specific modals
 * - Navigating to specific tabs
 * - Special URL transformations
 * 
 * If a type is not listed here, it will use the category default
 */
const TYPE_OVERRIDES = {
  // ==================== CLUB MANAGEMENT ====================
  
  // Join request received - admin should see the requests tab
  [NOTIFICATION_TYPES.CLUB_JOIN_REQUEST]: (ids) => ({
    url: `/club/${ids.clubId}`,
    state: { openModal: 'management', activeTab: 'requests' }
  }),

  // Join approved - member goes to club page
  [NOTIFICATION_TYPES.CLUB_JOIN_APPROVED]: (ids) => ({
    url: `/club/${ids.clubId}`
  }),

  // Join rejected - user goes to clubs listing
  [NOTIFICATION_TYPES.CLUB_JOIN_REJECTED]: () => ({
    url: '/clubs'
  }),

  // Payment due - go to payment section
  [NOTIFICATION_TYPES.CLUB_PAYMENT_DUE]: (ids) => ({
    url: `/club/${ids.clubId}`,
    state: { activeTab: 'payments' }
  }),

  // Role assigned - go to club page
  [NOTIFICATION_TYPES.CLUB_ROLE_ASSIGNED]: (ids) => ({
    url: `/club/${ids.clubId}`
  }),

  // Member removed - admin sees members list
  [NOTIFICATION_TYPES.CLUB_MEMBER_REMOVED]: (ids) => ({
    url: `/club/${ids.clubId}`,
    state: { openModal: 'management', activeTab: 'members' }
  }),

  // ==================== PROGRAM REGISTRATION ====================

  // Registration submitted - admin sees registrations
  [NOTIFICATION_TYPES.PROGRAM_REGISTRATION_SUBMITTED]: (ids) => {
    if (ids.programId && ids.clubId) {
      return { 
        url: `/clubs/${ids.clubId}/programs/${ids.programId}/manage`,
        state: { activeTab: 'registrations' }
      };
    }
    return CATEGORY_DEFAULTS[NOTIFICATION_CATEGORIES.PROGRAM](ids);
  },

  // Registration approved - user sees program
  [NOTIFICATION_TYPES.PROGRAM_REGISTRATION_APPROVED]: (ids) => {
    if (ids.programId && ids.clubId) {
      return { url: `/clubs/${ids.clubId}/programs/${ids.programId}` };
    }
    return CATEGORY_DEFAULTS[NOTIFICATION_CATEGORIES.PROGRAM](ids);
  },

  // Waitlisted - user sees their position
  [NOTIFICATION_TYPES.PROGRAM_REGISTRATION_WAITLISTED]: (ids) => {
    if (ids.programId && ids.clubId) {
      return { 
        url: `/clubs/${ids.clubId}/programs/${ids.programId}`,
        state: { showWaitlistInfo: true }
      };
    }
    return CATEGORY_DEFAULTS[NOTIFICATION_CATEGORIES.PROGRAM](ids);
  },

  // Capacity reached - admin sees program management
  [NOTIFICATION_TYPES.PROGRAM_CAPACITY_REACHED]: (ids) => {
    if (ids.programId && ids.clubId) {
      return { url: `/clubs/${ids.clubId}/programs/${ids.programId}/manage` };
    }
    return CATEGORY_DEFAULTS[NOTIFICATION_CATEGORIES.PROGRAM](ids);
  },

  // Document required - user goes to upload documents
  [NOTIFICATION_TYPES.PROGRAM_DOCUMENT_REQUIRED]: (ids) => {
    if (ids.programId && ids.clubId) {
      return { 
        url: `/clubs/${ids.clubId}/programs/${ids.programId}`,
        state: { openDocumentUpload: true }
      };
    }
    return CATEGORY_DEFAULTS[NOTIFICATION_CATEGORIES.PROGRAM](ids);
  },

  // ==================== EVENT MANAGEMENT ====================

  // Event reminder - go to event details
  [NOTIFICATION_TYPES.EVENT_REMINDER]: (ids) => {
    if (ids.eventId && ids.clubId) {
      return { url: `/clubs/${ids.clubId}/events/${ids.eventId}` };
    }
    return CATEGORY_DEFAULTS[NOTIFICATION_CATEGORIES.EVENT](ids);
  },

  // RSVP deadline - show RSVP modal
  [NOTIFICATION_TYPES.EVENT_RSVP_DEADLINE]: (ids) => {
    if (ids.eventId && ids.clubId) {
      return { 
        url: `/clubs/${ids.clubId}/events/${ids.eventId}`,
        state: { openRsvpModal: true }
      };
    }
    return CATEGORY_DEFAULTS[NOTIFICATION_CATEGORIES.EVENT](ids);
  },

  // Weather alert - event details with alert
  [NOTIFICATION_TYPES.EVENT_WEATHER_ALERT]: (ids) => {
    if (ids.eventId && ids.clubId) {
      return { 
        url: `/clubs/${ids.clubId}/events/${ids.eventId}`,
        state: { showWeatherAlert: true }
      };
    }
    return CATEGORY_DEFAULTS[NOTIFICATION_CATEGORIES.EVENT](ids);
  },

  // Results posted - show results tab
  [NOTIFICATION_TYPES.EVENT_RESULTS_POSTED]: (ids) => {
    if (ids.eventId && ids.clubId) {
      return { 
        url: `/clubs/${ids.clubId}/events/${ids.eventId}`,
        state: { activeTab: 'results' }
      };
    }
    return CATEGORY_DEFAULTS[NOTIFICATION_CATEGORIES.EVENT](ids);
  },

  // ==================== TEAM MANAGEMENT ====================

  // Team roster added - go to team page
  [NOTIFICATION_TYPES.TEAM_ROSTER_ADDED]: (ids) => {
    if (ids.teamId) {
      return { url: `/teams/${ids.teamId}` };
    }
    return { url: '/teams' };
  },

  // Team invitation - handled by TeamInvitationNotification component
  // This override is here for documentation; the component handles it specially
  [NOTIFICATION_TYPES.TEAM_INVITATION_SENT]: (ids) => {
    if (ids.teamId) {
      return { url: `/teams/${ids.teamId}` };
    }
    return { url: '/teams' };
  },

  // Practice scheduled - go to team schedule
  [NOTIFICATION_TYPES.TEAM_PRACTICE_SCHEDULED]: (ids) => {
    if (ids.teamId) {
      return { 
        url: `/teams/${ids.teamId}`,
        state: { activeTab: 'schedule' }
      };
    }
    return { url: '/teams' };
  },

  // Team announcement - go to team page
  [NOTIFICATION_TYPES.TEAM_ANNOUNCEMENT]: (ids) => {
    if (ids.teamId) {
      return { 
        url: `/teams/${ids.teamId}`,
        state: { activeTab: 'announcements' }
      };
    }
    return { url: '/teams' };
  },

  // ==================== EQUIPMENT MANAGEMENT ====================

  // Equipment assigned - view equipment details
  [NOTIFICATION_TYPES.EQUIPMENT_ASSIGNED]: (ids) => ({
    url: ids.clubId ? `/club/${ids.clubId}` : '/dashboard',
    state: { activeTab: 'equipment', showAssigned: true }
  }),

  // Equipment return due - show return modal
  [NOTIFICATION_TYPES.EQUIPMENT_RETURN_DUE]: (ids) => ({
    url: ids.clubId ? `/club/${ids.clubId}` : '/dashboard',
    state: { activeTab: 'equipment', openReturnModal: true }
  }),

  // Equipment overdue - urgent return needed
  [NOTIFICATION_TYPES.EQUIPMENT_OVERDUE]: (ids) => ({
    url: ids.clubId ? `/club/${ids.clubId}` : '/dashboard',
    state: { activeTab: 'equipment', openReturnModal: true, overdue: true }
  }),

  // ==================== SYSTEM NOTIFICATIONS ====================

  // Profile update required
  [NOTIFICATION_TYPES.PROFILE_UPDATE_REQUIRED]: () => ({
    url: '/profile',
    state: { showRequired: true }
  }),

  // Security alert - go to security settings
  [NOTIFICATION_TYPES.SECURITY_ALERT]: () => ({
    url: '/profile/security'
  }),

  // Password reset - go to password change
  [NOTIFICATION_TYPES.PASSWORD_RESET_REQUEST]: () => ({
    url: '/profile/security',
    state: { openPasswordChange: true }
  }),

  // Account suspended - show suspension info
  [NOTIFICATION_TYPES.ACCOUNT_SUSPENDED]: () => ({
    url: '/profile',
    state: { showSuspensionInfo: true }
  }),
};

// ==================== MAIN RESOLVER FUNCTION ====================

/**
 * Resolve notification action URL to a valid frontend route
 * 
 * Resolution priority:
 * 1. Check for type-specific override
 * 2. Fall back to category default
 * 3. Use the original URL if valid
 * 4. Ultimate fallback to dashboard
 * 
 * @param {Object} notification - The notification object
 * @param {string} actionUrl - The original action URL from backend
 * @returns {Object} { url: string, state?: object } - Resolved route
 */
export const resolveNotificationRoute = (notification, actionUrl) => {
  // Handle empty or invalid URLs
  if (!actionUrl || actionUrl === '#') {
    return { url: null };
  }

  // Extract all relevant IDs
  const ids = extractIds(notification, actionUrl);
  const notificationType = notification?.type;
  const category = notification?.category;

  // 1. Check for type-specific override
  if (notificationType && TYPE_OVERRIDES[notificationType]) {
    const result = TYPE_OVERRIDES[notificationType](ids);
    if (result.url) {
      return result;
    }
  }

  // 2. Fall back to category default
  if (category && CATEGORY_DEFAULTS[category]) {
    const result = CATEGORY_DEFAULTS[category](ids);
    if (result.url && result.url !== '/dashboard') {
      return result;
    }
  }

  // 3. Check if the original URL is a valid route pattern
  const validPatterns = [
    /^\/clubs?\/[a-f0-9]{24}$/i,
    /^\/clubs?\/[a-f0-9]{24}\/programs\/[a-f0-9]{24}$/i,
    /^\/clubs?\/[a-f0-9]{24}\/programs\/[a-f0-9]{24}\/manage$/i,
    /^\/clubs?\/[a-f0-9]{24}\/events\/[a-f0-9]{24}$/i,
    /^\/teams\/[a-f0-9]{24}$/i,
    /^\/teams$/i,
    /^\/profile/i,
    /^\/dashboard$/i,
    /^\/notifications$/i,
  ];

  if (validPatterns.some(pattern => pattern.test(actionUrl))) {
    return { url: actionUrl };
  }

  // 4. Ultimate fallback based on available IDs
  if (ids.clubId) {
    return { url: `/club/${ids.clubId}` };
  }
  if (ids.teamId) {
    return { url: `/teams/${ids.teamId}` };
  }

  // Default to dashboard
  return { url: '/dashboard' };
};

/**
 * Check if a notification has a navigable action
 * @param {Object} notification - The notification object
 * @returns {boolean} - True if notification can navigate somewhere
 */
export const hasNavigableAction = (notification) => {
  const actionUrl = notification?.data?.actionUrl || notification?.actionUrl;
  const result = resolveNotificationRoute(notification, actionUrl);
  return result.url && result.url !== '/dashboard';
};

/**
 * Get route info for debugging/logging
 * @param {Object} notification - The notification object
 * @returns {Object} - Debug info about the route resolution
 */
export const getRouteDebugInfo = (notification) => {
  const actionUrl = notification?.data?.actionUrl || notification?.actionUrl;
  const ids = extractIds(notification, actionUrl);
  const hasOverride = notification?.type && !!TYPE_OVERRIDES[notification.type];
  const result = resolveNotificationRoute(notification, actionUrl);
  
  return {
    originalUrl: actionUrl,
    resolvedUrl: result.url,
    state: result.state,
    ids,
    hasTypeOverride: hasOverride,
    category: notification?.category,
    type: notification?.type,
  };
};

export default resolveNotificationRoute;

